import { useRef, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Webcam from 'react-webcam';
import Swal from 'sweetalert2';
import * as faceapi from 'face-api.js';
import { FiCheckCircle, FiXCircle, FiLoader } from 'react-icons/fi';
import { BiFace } from 'react-icons/bi';
import { registerBiometric } from '../api/services';

const SIDES = [
  {
    id: 'front',
    label: 'Front',
    instruction: 'Look straight at the camera',
    icon: '👁️',
    validate: (pose) => Math.abs(pose.yaw) < 25 && Math.abs(pose.pitch) < 18,
  },
  {
    id: 'left',
    label: 'Left',
    instruction: 'Turn your head slightly to the left',
    icon: '◀️',
    validate: (pose) => pose.yaw > 12 && pose.yaw < 60,
  },
  {
    id: 'right',
    label: 'Right',
    instruction: 'Turn your head slightly to the right',
    icon: '▶️',
    validate: (pose) => pose.yaw < -12 && pose.yaw > -60,
  },
  // {
  //   id: 'top',
  //   label: 'Top',
  //   instruction: 'Tilt your head slightly upward',
  //   icon: '🔼',
  //   validate: (pose) => pose.pitch < 5 && pose.pitch > -45,
  // },
];

const MODELS_URL = '/weights';
const SCORE_THRESHOLD = 0.45;
const INPUT_SIZE = 320;
const CAPTURES_PER_SIDE = 3; // Require 3 captures per side
const HOLD_FRAMES = 1;   // immediate capture in the first valid frame
const MISS_TOLERANCE = 20;    // allow more transient misses without reset
const SMOOTH_ALPHA = 0.35; // EMA weight for yaw/pitch smoothing
let _tid = 0;

function useToast() {
  const [toasts, setToasts] = useState([]);
  const show = useCallback((msg, type = 'info') => {
    const id = ++_tid;
    setToasts(p => [...p, { id, msg, type }]);
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), 3000);
  }, []);
  return { toasts, show };
}

const TOAST_STYLE = {
  success: { bg: '#16a34a', icon: '✓' },
  warn: { bg: '#d97706', icon: '⚠' },
  error: { bg: '#dc2626', icon: '✕' },
  info: { bg: '#2563eb', icon: 'ℹ' },
};

function ToastStack({ toasts }) {
  if (!toasts.length) return null;
  return (
    <div style={{
      position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
      zIndex: 60, display: 'flex', flexDirection: 'column', gap: 8,
      width: 300, pointerEvents: 'none',
    }}>
      {toasts.map(({ id, msg, type }) => {
        const s = TOAST_STYLE[type] ?? TOAST_STYLE.info;
        return (
          <div key={id} style={{
            background: s.bg, color: '#fff', borderRadius: 12,
            padding: '10px 16px', fontSize: 13, fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: 8,
            boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
            animation: 'toastIn 0.2s ease-out both',
          }}>
            <span style={{ fontSize: 16, flexShrink: 0 }}>{s.icon}</span>
            <span>{msg}</span>
          </div>
        );
      })}
      <style>{`@keyframes toastIn{from{opacity:0;transform:translateY(-10px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}

let _modelsLoaded = false;

async function ensureModels() {
  if (_modelsLoaded) return;
  await faceapi.nets.tinyFaceDetector.loadFromUri(MODELS_URL);
  await faceapi.nets.faceLandmark68Net.loadFromUri(MODELS_URL);
  _modelsLoaded = true;
}

function estimatePose(landmarks) {
  const p = landmarks.positions;
  const lEye = p[36];
  const rEye = p[45];
  const nose = p[30];
  const chin = p[8];

  const eyeMidX = (lEye.x + rEye.x) / 2;
  const eyeMidY = (lEye.y + rEye.y) / 2;
  const faceW = Math.abs(rEye.x - lEye.x) || 1;
  const faceH = Math.abs(chin.y - eyeMidY) || 1;

  const yaw = ((nose.x - eyeMidX) / faceW) * 90;
  const pitch = ((nose.y - eyeMidY) / faceH) * 90 - 15;

  return { yaw, pitch };
}

const CameraCapture = ({
  onClose,
  userId,
  isOpen,
  setShowCameraModal,
  loadRegisteredBiometrics,
}) => {
  const { t } = useTranslation();
  const { toasts, show: showToast } = useToast();

  // ── Refs ──────────────────────────────────────────────────────────────────
  const webcamRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const holdRef = useRef({ side: null, count: 0 });
  const missRef = useRef(0);
  const completedRef = useRef([]);
  // Store an array of images for each side
  const capturedRef = useRef({});
  const lastWarnRef = useRef({});
  const hasFinishedRef = useRef(false);
  const smoothPoseRef = useRef(null);
  // Track which sides have had their API call dispatched (fire-and-forget per side)
  const submittedSidesRef = useRef(new Set());

  // ── State ─────────────────────────────────────────────────────────────────
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [deviceId, setDeviceId] = useState(null);
  // const [devices, setDevices] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [currentDir, setCurrentDir] = useState(null);
  const [holdProgress, setHoldProgress] = useState(0);
  // const [faceInFrame, setFaceInFrame] = useState(false);
  const [lightingOk, setLightingOk] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ── Derived ───────────────────────────────────────────────────────────────
  const allScanned = completed.length === SIDES.length;
  const currentSideIdx = Math.min(completed.length, SIDES.length - 1);
  const currentSide = allScanned ? null : SIDES[currentSideIdx];
  const poseValid = !!currentDir && !!currentSide && currentDir === currentSide.id;
  // const sideAlreadyDone = !!currentSide && completed.includes(currentSide.id);

  // ── Lighting ──────────────────────────────────────────────────────────────
  const checkLighting = useCallback(() => {
    const video = webcamRef.current?.video;
    if (!video || video.readyState < 2) return;
    const tmp = document.createElement('canvas');
    tmp.width = 80; tmp.height = 45;
    tmp.getContext('2d').drawImage(video, 0, 0, 80, 45);
    const { data } = tmp.getContext('2d').getImageData(0, 0, 80, 45);
    let sum = 0;
    for (let i = 0; i < data.length; i += 4)
      sum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    setLightingOk(sum / (data.length / 4) > 50);
  }, []);

  // ── Oval overlay ──────────────────────────────────────────────────────────
  const drawOval = useCallback((valid) => {
    const video = webcamRef.current?.video;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const rect = video.getBoundingClientRect();
    const DW = rect.width || 640;
    const DH = rect.height || 360;
    if (canvas.width !== DW || canvas.height !== DH) {
      canvas.width = DW; canvas.height = DH;
    }

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, DW, DH);

    const cx = DW / 2;
    const cy = DH / 2 - DH * 0.02;
    const rx = DW * 0.27;
    const ry = DH * 0.46;

    // Vignette outside oval
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, DW, DH);
    ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.fill('evenodd');
    ctx.restore();

    // Oval border
    ctx.save();
    ctx.beginPath();
    ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
    if (valid) {
      ctx.strokeStyle = '#22c55e';
      ctx.lineWidth = 4;
      ctx.shadowColor = '#22c55e';
      ctx.shadowBlur = 14;
      ctx.setLineDash([]);
    } else {
      ctx.strokeStyle = 'rgba(34,211,238,0.9)';
      ctx.lineWidth = 2.5;
      ctx.shadowColor = 'rgba(34,211,238,0.4)';
      ctx.shadowBlur = 8;
      ctx.setLineDash([14, 9]);
    }
    ctx.stroke();
    ctx.restore();

    // Progress arc sweeping clockwise from top
    const count = holdRef.current.count;
    if (count > 0) {
      const progress = count / HOLD_FRAMES;
      ctx.save();
      ctx.beginPath();
      ctx.save();
      ctx.translate(cx, cy);
      ctx.scale(rx, ry);
      ctx.arc(0, 0, 1, -Math.PI / 2, -Math.PI / 2 + progress * Math.PI * 2);
      ctx.restore();
      ctx.strokeStyle = '#22c55e';
      ctx.lineWidth = 5;
      ctx.shadowColor = '#22c55e';
      ctx.shadowBlur = 18;
      ctx.setLineDash([]);
      ctx.stroke();
      ctx.restore();
    }
  }, []);

  const clearCanvas = useCallback(() => {
    const c = canvasRef.current;
    if (c) c.getContext('2d').clearRect(0, 0, c.width, c.height);
  }, []);

  // ── Capture a single frame as a natural (non-mirrored) JPEG data URL ──────
  const captureFrame = useCallback(() => {
    const video = webcamRef.current?.video;
    if (!video || video.readyState < 2) return null;
    const c = document.createElement('canvas');
    c.width = video.videoWidth || 640;
    c.height = video.videoHeight || 480;
    c.getContext('2d').drawImage(video, 0, 0);
    return c.toDataURL('image/jpeg', 0.92);
  }, []);

  // ── Submit a single side to API immediately after capture ─────────────────
  // Sends: userId, type='FACE', image dataUrl, position (side id)
  const submitSide = useCallback(async (sideId, dataUrls) => {
    // Submit all images for this side
    for (let i = 0; i < dataUrls.length; i++) {
      try {
        await registerBiometric(userId, 'FACE', dataUrls[i], sideId);
      } catch (error) {
        const msg = error?.response?.data?.message ?? '';
        if (msg === 'FACE enrollemnt already completed') {
          setShowCameraModal({ isOpen: false });
          Swal.fire({
            icon: 'error',
            title: t('FACE enrollemnt already completed'),
            timer: 1800,
            customClass: {
              confirmButton: 'gradient-button'
            }
          });
          return;
        }
        // For individual side errors, show toast and continue (don't close modal)
        // showToast(`Failed to upload ${sideId} (capture ${i + 1}): ${msg || 'Server error'}`, 'error');
      }
    }
  }, [userId, t, setShowCameraModal]);

  // ── Called after all 4 sides are captured — waits briefly then shows success ─
  const finishRegistration = useCallback(async () => {
    if (hasFinishedRef.current) return;
    hasFinishedRef.current = true;
    setIsSubmitting(true);
    clearCanvas();
    setIsSubmitting(false);
    setShowCameraModal({ isOpen: false });
    Swal.fire({
      icon: 'success',
      title: t('Face registered successfully'),
      timer: 1800,
      customClass: {
        confirmButton: 'gradient-button'
      }
    });
    await loadRegisteredBiometrics();
  }, [t, setShowCameraModal, loadRegisteredBiometrics, clearCanvas]);

  // ── rAF detection loop ────────────────────────────────────────────────────
  const detect = useCallback(async () => {
    const video = webcamRef.current?.video;
    if (!video || video.readyState < 2 || !video.srcObject) {
      rafRef.current = requestAnimationFrame(detect);
      return;
    }

    checkLighting();

    let detection = null;
    try {
      detection = await faceapi
        .detectSingleFace(video, new faceapi.TinyFaceDetectorOptions({ inputSize: INPUT_SIZE, scoreThreshold: SCORE_THRESHOLD }))
        .withFaceLandmarks();
    } catch (_) {
      rafRef.current = requestAnimationFrame(detect);
      return;
    }

    // ── No face detected ──────────────────────────────────────────────────
    if (!detection) {
      missRef.current++;
      // setFaceInFrame(false);
      if (missRef.current >= MISS_TOLERANCE) {
        holdRef.current = { side: null, count: 0 };
        setHoldProgress(0);
        setCurrentDir(null);
        smoothPoseRef.current = null;
      }
      drawOval(false);
      rafRef.current = requestAnimationFrame(detect);
      return;
    }

    missRef.current = 0;
    // setFaceInFrame(true);

    // ── EMA pose smoothing ────────────────────────────────────────────────
    const raw = estimatePose(detection.landmarks);
    smoothPoseRef.current = !smoothPoseRef.current
      ? { ...raw }
      : {
        yaw: SMOOTH_ALPHA * raw.yaw + (1 - SMOOTH_ALPHA) * smoothPoseRef.current.yaw,
        pitch: SMOOTH_ALPHA * raw.pitch + (1 - SMOOTH_ALPHA) * smoothPoseRef.current.pitch,
      };
    const pose = smoothPoseRef.current;

    // ── Classify direction ────────────────────────────────────────────────
    let dir = null;
    for (const side of SIDES) {
      if (side.validate(pose)) { dir = side.id; break; }
    }
    setCurrentDir(dir);

    if (dir) {
      const done = completedRef.current;
      const idx = SIDES.findIndex(s => s.id === dir);
      const prev = idx > 0 ? SIDES[idx - 1] : null;
      const now = Date.now();

      // ── Block: previous side not yet done ────────────────────────────
      if (prev && !done.includes(prev.id)) {
        const key = 'order_' + dir;
        if (!lastWarnRef.current[key] || now - lastWarnRef.current[key] > 2500) {
          lastWarnRef.current[key] = now;
          showToast(`Complete "${prev.label}" first`, 'warn');
        }
        drawOval(false);
        rafRef.current = requestAnimationFrame(detect);
        return;
      }

      // ── Block: duplicate ──────────────────────────────────────────────
      if (done.includes(dir)) {
        const key = 'dup_' + dir;
        if (!lastWarnRef.current[key] || now - lastWarnRef.current[key] > 2500) {
          lastWarnRef.current[key] = now;
          showToast(`"${SIDES.find(s => s.id === dir)?.label}" already scanned!`, 'warn');
        }
        drawOval(false);
        rafRef.current = requestAnimationFrame(detect);
        return;
      }

      // ── CAPTURE — accumulate up to 3 per side ───────
      let captures = capturedRef.current[dir] || [];
      if (captures.length < CAPTURES_PER_SIDE) {
        const snapshot = captureFrame();
        if (snapshot) {
          captures = [...captures, snapshot];
          capturedRef.current = { ...capturedRef.current, [dir]: captures };
          showToast(`✓ ${SIDES.find(s => s.id === dir)?.label} capture ${captures.length}/${CAPTURES_PER_SIDE}!`, 'success');
        }
      }

      // Only submit and mark as done after 3 captures
      if (captures.length === CAPTURES_PER_SIDE && !submittedSidesRef.current.has(dir)) {
        submittedSidesRef.current.add(dir);
        submitSide(dir, captures);
        const newDone = [...done, dir];
        completedRef.current = newDone;
        setCompleted([...newDone]);
        smoothPoseRef.current = null;
        setHoldProgress(0);

        if (newDone.length === SIDES.length) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
          finishRegistration();
          return;
        }
      }

      drawOval(true);

    } else {
      drawOval(false);
    }

    rafRef.current = requestAnimationFrame(detect);
  }, [checkLighting, drawOval, captureFrame, submitSide, finishRegistration, showToast]);

  // ── Camera devices ────────────────────────────────────────────────────────
  const getCameraDevices = useCallback(async () => {
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      const cams = all.filter(d => d.kind === 'videoinput');
      // setDevices(cams);
      if (cams.length > 0) setDeviceId(cams[0].deviceId);
    } catch (err) { console.error(err); }
  }, []);

  // ── Load models once on open ──────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    getCameraDevices();
    ensureModels()
      .then(() => setModelsLoaded(true))
      .catch(err => console.error('Model load failed:', err));
  }, [isOpen, getCameraDevices]);

  // ── Start rAF loop once camera + models are both ready ────────────────────
  useEffect(() => {
    if (!cameraReady || !modelsLoaded || !isOpen) return;

    // Hard reset of all scan state
    completedRef.current = [];
    capturedRef.current = {};
    holdRef.current = { side: null, count: 0 };
    missRef.current = 0;
    lastWarnRef.current = {};
    hasFinishedRef.current = false;
    smoothPoseRef.current = null;
    submittedSidesRef.current = new Set();
    setCompleted([]);
    setCurrentDir(null);
    setHoldProgress(0);
    // setFaceInFrame(false);

    rafRef.current = requestAnimationFrame(detect);
    return () => { cancelAnimationFrame(rafRef.current); rafRef.current = null; };
  }, [cameraReady, modelsLoaded, isOpen, detect]);

  // ── Cleanup on unmount ────────────────────────────────────────────────────
  useEffect(() => {
    return () => { cancelAnimationFrame(rafRef.current); rafRef.current = null; };
  }, []);

  // ── Webcam handlers ───────────────────────────────────────────────────────
  const handleUserMedia = useCallback(() => {
    setCameraReady(true);
    setCameraError(null);
  }, []);

  const handleUserMediaError = useCallback((err) => {
    setCameraError(err.message ?? 'Camera error');
    setCameraReady(false);
    Swal.fire({
      icon: 'error',
      title: t('Camera Error'),
      text: t('Unable to access camera. Please check permissions and try again.'),
      customClass: {
        confirmButton: 'gradient-button'
      }
    });
  }, [t]);

  console.log(lightingOk);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden">

        {/* Header */}
        <div className="bg-gradient-to-r from-[#B03982] to-[#733C86] p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <BiFace className="text-3xl" />
              <h2 className="text-2xl font-bold">{t('Face Registration')}</h2>
            </div>
            <button
              onClick={() => onClose(false)}
              className="p-2 hover:bg-blue-500 rounded-full transition-colors"
              disabled={isSubmitting}
            >
              <FiXCircle className="text-2xl" />
            </button>
          </div>
          <p className="mt-2 text-blue-100">
            {isSubmitting
              ? t('Registering your face — please wait…')
              : allScanned
                ? t('All sides captured — finalising…')
                : t('Follow the on-screen instructions to scan all sides of your face')}
          </p>
        </div>

        {/* Body */}
        <div className="p-6">

          {/* Progress pills */}
          <div className="flex gap-2 mb-3 flex-wrap">
            {SIDES.map((side, idx) => {
              const done = completed.includes(side.id);
              const active = idx === currentSideIdx && !allScanned;
              return (
                <div
                  key={side.id}
                  className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium border transition-all
                        ${done ? 'bg-green-100 border-green-400 text-green-700'
                      : active ? 'bg-blue-100 border-blue-500 text-blue-700'
                        : 'bg-gray-100 border-gray-300 text-gray-400'}`}
                >
                  <span>{side.icon}</span>
                  <span>{t(side.label)}</span>
                  {done && <FiCheckCircle className="text-green-500" />}
                </div>
              );
            })}
          </div>

          {/* Video area */}
          <div className="relative bg-black rounded-xl overflow-hidden w-[600px] h-[400px]">

            <ToastStack toasts={toasts} />

            {/* Video — CSS mirror for selfie preview only */}
            <Webcam
              ref={webcamRef}
              audio={false}
              screenshotFormat="image/jpeg"
              videoConstraints={{
                width: { ideal: 1080 },
                height: { ideal: 600 },
                facingMode: 'user',
                deviceId,
                aspectRatio: 16 / 9,
              }}
              onUserMedia={handleUserMedia}
              onUserMediaError={handleUserMediaError}
              className="w-full h-full object-cover"
              style={{ transform: 'scaleX(-1)' }}
            />

            {/* Canvas overlay */}
            <canvas
              ref={canvasRef}
              className="absolute inset-0 w-full h-full"
              style={{ pointerEvents: 'none' }}
            />

            {/* Hold progress bar */}
            {holdProgress > 0 && !isSubmitting && (
              <div className="absolute bottom-5 left-1/2 -translate-x-1/2 w-44">
                <div className="w-full bg-white/20 rounded-full h-2.5">
                  <div
                    className="bg-green-400 h-2.5 rounded-full transition-all duration-75"
                    style={{ width: `${holdProgress * 100}%` }}
                  />
                </div>
                <p className="text-white text-xs text-center mt-1.5 font-semibold drop-shadow">
                  {t('Hold still…')}
                </p>
              </div>
            )}

            {/* Direction prompt */}
            {currentSide && !isSubmitting && modelsLoaded && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent pt-8 pb-3 px-4 text-center pointer-events-none">
                <p className={`text-sm font-bold tracking-wide drop-shadow ${poseValid ? 'text-green-300' : 'text-white'}`}>
                  {poseValid
                    ? `✅ ${t('Hold still to capture…')}`
                    : `${currentSide.icon}  ${t(currentSide.instruction)}`}
                </p>
              </div>
            )}

            {/* Loading overlay */}
            {!modelsLoaded && (
              <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center gap-3">
                <FiLoader className="animate-spin text-4xl text-cyan-400" />
                <p className="text-cyan-300 text-sm font-semibold tracking-widest uppercase">{t('Loading face detection…')}</p>
                <p className="text-gray-400 text-xs px-8 text-center">{t('Downloading models on first use…')}</p>
              </div>
            )}

            {/* Camera error */}
            {cameraError && (
              <div className="absolute inset-0 bg-red-900/80 flex flex-col items-center justify-center gap-3 px-6 text-center">
                <FiXCircle className="text-5xl text-red-300" />
                <p className="text-white font-semibold">{t('Camera Error')}</p>
                <p className="text-red-200 text-xs">{cameraError}</p>
                <button
                  onClick={getCameraDevices}
                  className="mt-2 px-5 py-2 bg-white text-red-700 rounded-lg text-sm font-semibold hover:bg-gray-100"
                >
                  {t('Retry')}
                </button>
              </div>
            )}

            {/* Submission overlay */}
            {isSubmitting && (
              <div className="absolute inset-0 bg-black/75 flex items-center justify-center">
                <div className="bg-white rounded-2xl p-6 max-w-xs w-full mx-4 text-center shadow-2xl">
                  <FiLoader className="animate-spin text-4xl text-blue-600 mx-auto mb-3" />
                  <h3 className="text-lg font-semibold text-gray-800 mb-1">{t('Registering Face…')}</h3>
                  <p className="text-sm text-gray-500">{t('Finalising registration…')}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CameraCapture;